import React, { useEffect, useState } from 'react';
import client, { urlFor } from '../sanityClient';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Modal from './Modal';
import AdminOffersModal from './AdminOffersModal';

const ItemDetailPage = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInquiry, setShowInquiry] = useState(false);
  const [showAdminOffers, setShowAdminOffers] = useState(false);

  const formatSeason = (season) => {
    if (!season) return '';
    if (season.toLowerCase().includes('autumn/winter')) return 'AW';
    if (season.toLowerCase().includes('spring/summer')) return 'SS';
    return season;
  };

  useEffect(() => {
    async function fetchItem() {
      setLoading(true);
      const data = await client.fetch('*[_type == "item" && _id == $id][0]', { id });
      setItem(data || null);
      setLoading(false);
    }
    fetchItem();
  }, [id]);

  // Update document title when item loads
  useEffect(() => {
    if (item) {
      const title = `${item.designer} ${formatSeason(item.season)} ${item.year} ${item.itemDetails && item.itemDetails.size ? item.itemDetails.size : ''} ${item.itemDetails && item.itemDetails.color ? item.itemDetails.color : ''} ${(item.itemDetails && item.itemDetails.components ? ' ' + item.itemDetails.components.replace(',', ' &') : '')} | Titled Works`;

      document.title = title;
    } else {
      document.title = 'Titled Works | Curator Access';
    }
  }, [item]);

  useEffect(() => {
    if (location.hash === '#offer') {
      setShowInquiry(true);
    }
  }, [location.hash]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!item) return <div className="min-h-screen flex items-center justify-center text-red-600">Item not found.</div>;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header with Close Button */}
      <div className="flex items-center justify-start w-full mt-4 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-gray-600 text-sm transition-colors flex items-center gap-2"
          aria-label="Close"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Gallery
        </button>
      </div>
      <div className="max-w-7xl mx-auto pt-4 pb-10 px-4 w-full sm:px-6 lg:px-8 flex flex-col-reverse md:flex-row gap-8 flex-1">
        {/* Gallery Single Column, Scrollable */}
        <div className="md:w-1/2 flex flex-col">
          <div className="flex flex-col gap-2 overflow-y-auto pr-2">
            {item.images && item.images.length > 0 && (
              <div className="flex flex-wrap gap-4 mb-4 relative">
                {/* on first image, add status tag */}
                {item.status && (
                  <span className={`absolute top-5 right-5 z-10 inline-block mb-2 px-2 py-1 rounded-full text-xs font-medium ${item.status == 'Sold' ? 'bg-red-100 text-red-800' : item.status === 'On Hold' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>{item.status}</span>
                )}
                {item.images.map((img, idx) => (
                  <React.Fragment key={idx}>
                    {/* Always show first image */}
                    {idx === 0 && (
                      <img
                        src={urlFor(img).width(800).url()}
                        alt={item.title}
                        className="rounded-lg w-full"
                        onContextMenu={e => e.preventDefault()}
                      />
                    )}
                    {/* Show additional images only if not sold */}
                    {item.status !== 'Sold' && idx > 0 && (
                      <img
                        src={urlFor(img).width(800).url()}
                        alt={item.title}
                        className="rounded-lg w-full"
                        onContextMenu={e => e.preventDefault()}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Details */}
        <div className="md:w-1/2 flex flex-col justify-start md:sticky md:top-32 md:pt-12 self-start">
          <div className="max-w-[500px] w-full mx-auto">
            <div className="w-full">
              <h2 className="text-3xl md:text-5xl mb-3 text-gray-900">
                <a
                  href={`/?designer=${encodeURIComponent(item.designer.toLowerCase().replace(/ /g, '-') )}`}
                  className="no-underline"
                  title={`View all by ${item.designer}`}
                >
                  {item.designer}
                </a>
              </h2>
              <div className="flex flex-row gap-2 mb-3">
                  <div className="text-sm text-gray-600 mb3">
                    {formatSeason(item.season)}  {item.year}
                  </div>
                  {item.leadtime && (
                    <div className="flex flex-row gap-2">
                      <span className="text-xs text-gray-800">•</span>
                      <span className="text-sm text-gray-800">
                        {item.leadtime.includes('days') || item.leadtime.includes('week') ? "Lead time: " + item.leadtime : item.leadtime }
                      </span>
                    </div>
                  )}
              </div>
              <div className="mb-4 text-gray-700 text-sm mb-8 mt-2">{item.description}</div>
              {item.showPrice && item.price && (
                <div className="text-xl text-gray-900 mb-2 mt-4">Seller price: ${item.price.toLocaleString()}</div>
              )}
              {/* Fabric Info Table */}
              {item.itemDetails && (
                <div className="mt-4">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    {Object.entries(item.itemDetails)
                      .filter(([key]) => key.toLowerCase() !== 'notes' && key.toLowerCase() !== '_type')
                      .map(([key, value]) => (
                        <React.Fragment key={key}>
                          <div className="text-gray-600 font-medium capitalize">{key}</div>
                          <div className="text-gray-900">{value}</div>
                        </React.Fragment>
                      ))}
                  </div>
                  {'notes' in item.itemDetails && item.itemDetails.notes && (
                    <div className="mt-6 text-sm">
                      <div className="text-gray-600 font-medium mb-1">Notes</div>
                      <div className="text-gray-900 whitespace-pre-line max-w-[450px]">{item.itemDetails.notes}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="mt-6 flex space-x-3">
              <button
                className="btn-primary flex-1 text-sm py-4 px-4 disabled:opacity-40"
                onClick={() => {
                  if (user?.admin) {
                    setShowAdminOffers(true);
                  } else {
                    setShowInquiry(true);
                  }
                }}
              >
                {user?.admin ? 'View Offers' : item.status === 'Sold' ? 'Inquire' : 'Inquire / Make Offer'}
              </button>
            </div>
          </div>
        </div>
        {/* Inquiry/Offer Modal */}
        <Modal
          isOpen={showInquiry}
          onClose={() => setShowInquiry(false)}
          item={item}
          userEmail={user?.email}
        />
        {/* Admin offers modal */}
        {user?.admin && (
          <AdminOffersModal
            isOpen={showAdminOffers}
            onClose={() => setShowAdminOffers(false)}
            itemId={item._id}
            itemTitle={item.designer + ' ' + item.season + ' ' + item.year + (item.itemDetails && item.itemDetails.components ? ' ' + item.itemDetails.components.replace(',', ' &') : '')}
          />
        )}
      </div>
    </div>
  );
};

export default ItemDetailPage; 